import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  resetAddProduct,
  resetEditProduct,
  setAddProductModal,
} from "../products.slice";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CalendarIcon, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { addProduct, getProducts, updateProduct } from "../products.action";
import { useEffect } from "react";

const FormSchema = z.object({
  name: z
    .string({
      required_error: "Name is required",
    })
    .min(1, {
      message: "Name is required",
    }),
  batch: z
    .string({
      required_error: "Batch is required",
    })
    .min(1, {
      message: "Batch is required",
    }),
  expiryDate: z.string().transform((val) => new Date(val).toISOString()),
  pkg: z
    .string({
      required_error: "Package is required",
    })
    .min(1, {
      message: "Package is required",
    }),
  manufacturer: z
    .string({
      required_error: "Manufacturer is required",
    })
    .min(1, {
      message: "Manufacturer is required",
    }),
  costPrice: z
    .number({
      required_error: "Cost Price is required",
      invalid_type_error: "Cost Price must be a number",
    })
    .positive(),
  retailCostPrice: z
    .number({
      required_error: "Retail Cost Price is required",
      invalid_type_error: "Retail Cost Price must be a number",
    })
    .positive(),
  mrp: z
    .number({
      required_error: "MRP is required",
      invalid_type_error: "MRP must be a number",
    })
    .positive(),
  hsn: z
    .number({
      required_error: "HSN is required",
    })
    .min(1, {
      message: "HSN is required",
    }),
  cgst: z
    .number({
      required_error: "CGST is required",
      invalid_type_error: "CGST must be a number",
    })
    .positive(),
  sgst: z
    .number({
      required_error: "SGST is required",
      invalid_type_error: "SGST must be a number",
    })
    .positive(),
});

export default function AddProductModal() {
  // Hooks
  const dispatch = useAppDispatch();

  // Redux
  const {
    addProductModal,
    isAddProductLoading,
    addProductSuccess,
    addProductError,
    isEditProductLoading,
    editProductSuccess,
    editProductError,
    selectedProduct,
    filters,
  } = useAppSelector((state) => state.products);

  const form = useForm({
    resolver: zodResolver(FormSchema),
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    if (selectedProduct) {
      dispatch(updateProduct({ id: selectedProduct._id, payload: data }));
    } else {
      dispatch(addProduct(data));
    }
  }

  useEffect(() => {
    form.setValue("name", selectedProduct?.name || "");
    form.setValue("batch", selectedProduct?.batch || "");
    form.setValue("expiryDate", selectedProduct?.expiryDate || "");
    form.setValue("pkg", selectedProduct?.pkg || "");
    form.setValue("manufacturer", selectedProduct?.manufacturer || "");
    form.setValue("costPrice", selectedProduct?.costPrice || 0);
    form.setValue("retailCostPrice", selectedProduct?.retailCostPrice || 0);
    form.setValue("mrp", selectedProduct?.mrp || 0);
    form.setValue("hsn", selectedProduct?.hsn || 0);
    form.setValue("cgst", selectedProduct?.cgst || 0);
    form.setValue("sgst", selectedProduct?.sgst || 0);
  }, [selectedProduct]);

  useEffect(() => {
    if (addProductError) {
      toast.error(addProductError);
    }

    if (addProductSuccess) {
      toast.success("Product added successfully");
      dispatch(setAddProductModal(false));
      dispatch(getProducts(filters));
      form.reset();
    }

    return () => {
      dispatch(resetAddProduct());
    };
  }, [addProductSuccess, addProductError]);

  useEffect(() => {
    if (editProductError) {
      toast.error(editProductError);
    }

    if (editProductSuccess) {
      toast.success("Product updated successfully");
      dispatch(setAddProductModal(false));
      dispatch(getProducts(filters));
      form.reset();
    }

    return () => {
      dispatch(resetEditProduct());
    };
  }, [editProductSuccess, editProductError]);

  return (
    <Dialog
      open={addProductModal}
      onOpenChange={(e) => dispatch(setAddProductModal(e))}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {selectedProduct ? "Edit Product" : "Add Product"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="grid grid-cols-12 gap-6"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="col-span-12">
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="batch"
              render={({ field }) => (
                <FormItem className="col-span-6">
                  <FormLabel>Batch</FormLabel>
                  <FormControl>
                    <Input placeholder="Batch" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="expiryDate"
              render={({ field }) => (
                <FormItem className="col-span-6">
                  <FormLabel>Expiry Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span className="text-muted-foreground">
                            Pick a date
                          </span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <FormControl>
                        <Calendar
                          mode="single"
                          selected={
                            field.value ? new Date(field.value) : undefined
                          }
                          onSelect={(e) => field.onChange(e?.toISOString())}
                          disabled={(date) =>
                            date < new Date() || date < new Date("1900-01-01")
                          }
                        />
                      </FormControl>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="pkg"
              render={({ field }) => (
                <FormItem className="col-span-6">
                  <FormLabel>Package</FormLabel>
                  <FormControl>
                    <Input placeholder="Package" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="manufacturer"
              render={({ field }) => (
                <FormItem className="col-span-6">
                  <FormLabel>Manufacturer</FormLabel>
                  <FormControl>
                    <Input placeholder="Manufacturer" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="costPrice"
              render={({ field }) => (
                <FormItem className="col-span-4">
                  <FormLabel>Cost Price</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Cost Price"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="retailCostPrice"
              render={({ field }) => (
                <FormItem className="col-span-4">
                  <FormLabel>Retail Cost Price</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Retail Cost Price"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="mrp"
              render={({ field }) => (
                <FormItem className="col-span-4">
                  <FormLabel>MRP</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="MRP"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hsn"
              render={({ field }) => (
                <FormItem className="col-span-4">
                  <FormLabel>HSN</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="HSN"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cgst"
              render={({ field }) => (
                <FormItem className="col-span-4">
                  <FormLabel>CGST</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="CGST"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sgst"
              render={({ field }) => (
                <FormItem className="col-span-4">
                  <FormLabel>SGST</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="SGST"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="col-span-12">
              <Button
                type="submit"
                disabled={isAddProductLoading || isEditProductLoading}
              >
                {selectedProduct ? "Edit Product" : "Add Product"}
                {(isAddProductLoading || isEditProductLoading) && (
                  <Loader2 className="animate-spin" />
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
